import {
  Card,
  CardBody,
  Col,
  Input,
  Row,
} from "reactstrap";

import { toast } from "react-toastify";
import { useExportSystemLogsMutation, useGetSystemLogsQuery } from "../../../../api/Endpoints/Core/App/SystemLogs";
import { formatDate, formatTime } from "@/helpers/date_helper";

import useCustomHook from "@/components/Hooks/useCustomHook";
import { AvatarIcon } from "@/components/Common/AvatarIcon";
import ExportExcelModal from "@/components/Common/Modals/ExportExcelModal";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { DatePickerField } from "../../Inputs";
import { useForm, useWatch } from "react-hook-form";

const MODULE_NAME = "Activities";

const UserLogsList = ({ userId }) => {
  const { state, customFunction } = useCustomHook()
  const [exportList] = useExportSystemLogsMutation()

  const defaultValues = {
    dateTo: new Date().toISOString(),
    dateFrom: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
  };

  const { control, reset, trigger, watch } = useForm({ defaultValues });

  const dateFrom = useWatch({ control, name: "dateFrom" });
  const dateTo = useWatch({ control, name: "dateTo" });

  const [isSearching, setIsSearching] = useState(false);
  const hasMore = state?.rowData.length < state?.totalRecords;
  const { data, isFetching } = useGetSystemLogsQuery({
    ...state.pageDetails, userId, dateFrom: dateFrom,
    dateTo: dateTo,
  }, { refetchOnMountOrArgChange: true })


  useEffect(() => {
    reset(defaultValues);
  }, []);

  useEffect(() => {
    trigger("dateFrom");
    trigger("dateTo");
  }, [dateFrom, dateTo]);

  useEffect(() => {
    customFunction.updateState({
      pageDetails: {
        ...state.pageDetails,
        page: 1,
      },
      rowData: [],
      totalRecords: 0,
    });
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (data?.items && !isSearching) {
      const isFirstPage = state.pageDetails.page === 1;

      customFunction.updateState({
        rowData: isFirstPage ? data.items : [...state.rowData, ...data.items],
        totalRecords: data.totalRecords,
      });
    }
  }, [data?.items, data?.totalRecords, state.pageDetails.page, isSearching, dateFrom,
    dateTo, state?.pageDetails?.keyword,]);

  const fetchNext = () => {
    if (hasMore && !isFetching && !isSearching) {
      customFunction.updateState({
        pageDetails: {
          ...state.pageDetails,
          page: state.pageDetails.page + 1,
        },
      });
    }
  };

  const ActivityItem = ({ logData }) => {
    return (

      <div
        className="acitivity-item py-3 d-flex"
      >
        <AvatarIcon name={logData.fullName} avatarImg={logData?.avatar} color={logData.badgeColor} />
        <div className="flex-grow-1 ms-3">
          <h6 className="mb-1">{logData.fullName}</h6>
          <small className="text-muted mb-2">
            {logData.title}{" "}
            {logData.badgeText && (
              <span
                className={`badge bg-${logData.badgeColor}-subtle text-${logData.badgeColor} align-middle`}
              >
                {logData.badgeText}
              </span>
            )}
          </small>
          <p
            className="mb-2"
            dangerouslySetInnerHTML={{
              __html: logData.description,
            }}
          />
          {logData.remarks && (
            <p
              className="mb-2 fst-italic"
              dangerouslySetInnerHTML={{
                __html: `"${logData.remarks}"`,
              }}
            />
          )}
          <small className="mb-0">
            {formatDate(logData.logDate)},
            <small className="text-muted">
              {" "}
              {formatTime(logData.logDate)}
            </small>
          </small>
        </div>
      </div>
    )
  }

  const handleSearchLogs = (e) => {
    const searchValue = e.target?.value || '';
    const { key } = e;

    if (key === "Enter") {
      customFunction.updateState({
        pageDetails: {
          ...state.pageDetails,
          keyword: searchValue,
          page: 1,
        }
      })
    }
  }

  const handleExport = async (e) => {
    if (e) {
      try {
        const res = await exportList({
          keyword: state.pageDetails.keyword,
          userId: userId,
        });

        toast(res ? `${MODULE_NAME} was successfully downloaded.` : `${MODULE_NAME} failed to download.`, {
          position: "top-right",
          hideProgressBar: false,
          closeOnClick: true,
          className: `${res ? 'bg-success' : 'bg-danger'} text-white`
        });
      } catch (error) {
        toast.error(`Export failed: ${error.message}`);
      }
    }
    customFunction.updateToggle("toggleExport");
  };

  return (
    <>
      <ExportExcelModal
        show={state.toggle?.toggleExport}
        onCloseClick={handleExport}
        title={`Export List of ${MODULE_NAME}`}
      />

      <Card>
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h5 className="card-title flex-grow-1 mb-0" >Activities</h5>.

            <div className="col-sm-auto ms-auto">
              <div>
                <button type="button" className="btn btn-info" onClick={() => customFunction.updateToggle('toggleExport')}>
                  <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                  Export
                </button>
              </div>
            </div>
          </div>

          <Row>
            <Col xl={12} lg={12}>
              <Card>
                <div className="card-body pt-0">

                  <CardBody className="border border-dashed border-end-0 border-start-0">
                    <Row className={"mt-10"}>
                      <Col lg={4}>
                        <DatePickerField
                          control={control}
                          name="dateFrom"
                          label="Date From"
                          placeholder="Date From..."
                          options={{
                            maxDate: new Date(),
                          }}
                          rules={{
                            validate: (value) => {
                              const dateFrom = new Date(value);
                              const dateToRaw = watch("dateTo");

                              if (!dateToRaw) return true;

                              const dateTo = new Date(dateToRaw);

                              if (isNaN(dateFrom.getTime()) || isNaN(dateTo.getTime())) {
                                return "Invalid date format";
                              }

                              if (dateFrom > dateTo) {
                                return "Date From should not be greater than Date To";
                              }

                              return true;
                            },
                          }}
                        />
                      </Col>
                      <Col lg={4}>
                        <DatePickerField
                          control={control}
                          name="dateTo"
                          label="Date To"
                          placeholder="Date To..."
                          options={{
                            maxDate: new Date(),
                          }}
                        />
                      </Col>
                      <Col lg={4} sm={5}>
                        <label className="form-label">Search</label>
                        <div className={"search-box me-2 mb-2 d-inline-block w-100"}>
                          <Input className="form-control bg-light border-light"
                            type='text'
                            placeholder={"Search here..."}
                            onKeyDown={handleSearchLogs}
                          />
                          <i className="bx bx-search-alt search-icon"></i>
                        </div>
                      </Col>

                    </Row>

                    <Row style={{ "marginTop": "30px" }}>
                      <InfiniteScroll
                        dataLength={state.rowData?.length}
                        next={fetchNext}
                        hasMore={hasMore && !isFetching && !isSearching}
                        scrollableTarget="scrollableDiv"
                        height={500}
                      >
                        <div id="scrollableDiv" className="acitivity-timeline">
                          {state.rowData?.length ? (
                            state.rowData?.map((logData, index) => (
                              <ActivityItem key={index} logData={logData} />
                            ))
                          ) : (
                            <center>
                              <span className="text-muted">
                                {isFetching || isSearching ? "Loading..." : "No record found"}
                              </span>
                            </center>
                          )}
                        </div>
                      </InfiniteScroll>
                    </Row>
                  </CardBody>
                </div>
              </Card>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default UserLogsList;
